/**
 * Promo-aware pricing for Simple PDP cover overflow flyout only.
 * SCAPI cover-variant responses may expose `price`, `pricePerUnit`, or
 * `productPromotions[].promotionalPrice` — keep this scoped here so global
 * `getProductEffectivePrice` / `computeCustomizeListTotal` stay unchanged.
 */

const isNumber = (value) => typeof value === 'number';

function parsePriceProcessed(product) {
  const raw = product?.c_priceProcessed;

  if (!raw) return null;

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

    return parsed != null && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function extractListSalePair(product) {
  const parsed = parsePriceProcessed(product);

  if (parsed != null) {
    const listPrice = isNumber(parsed.listPrice) ? parsed.listPrice : null;
    const salePrice = isNumber(parsed.salePrice) ?
      parsed.salePrice :
      isNumber(parsed.min) ?
        parsed.min :
        null;

    if (listPrice != null || salePrice != null) {
      return {listPrice, salePrice};
    }
  }

  const prices = product?.prices;

  if (!prices || typeof prices !== 'object') {
    return null;
  }

  const listPrice = isNumber(prices.listPrice) ? prices.listPrice : null;
  const salePrice = isNumber(prices.salePrice) ? prices.salePrice : null;

  return listPrice != null || salePrice != null ? {listPrice, salePrice} : null;
}

function resolveScapiBookPrice(product) {
  if (!product) return null;

  if (isNumber(product.price)) return product.price;
  if (isNumber(product.pricePerUnit)) return product.pricePerUnit;

  const pair = extractListSalePair(product);

  if (!pair) return null;

  if (isNumber(pair.salePrice)) return pair.salePrice;
  if (isNumber(pair.listPrice)) return pair.listPrice;

  return null;
}

function getPromotionalPrice(product) {
  const promos = product?.productPromotions;

  if (!Array.isArray(promos) || promos.length === 0) return null;

  const prices = promos
    .map((p) => p.promotionalPrice)
    .filter((p) => p != null && p > 0);

  if (!prices.length) return null;

  return Math.min(...prices);
}

/** Lowest promotionalPrice from SCAPI productPromotions. */
export function getSimplePdpCoverFlyoutPromoPrice(product) {
  return getPromotionalPrice(product);
}

/** After-promotion effective price from a flyout SCAPI cover-variant fetch. */
export function getSimplePdpCoverFlyoutEffectivePrice(product) {
  if (!product) return null;

  const basePrice = resolveScapiBookPrice(product);
  const promoPrice = getPromotionalPrice(product);

  if (promoPrice != null && (basePrice == null || promoPrice < basePrice)) {
    return promoPrice;
  }

  return basePrice;
}

/** List / RRP for strikethrough in the flyout footer. */
export function getSimplePdpCoverFlyoutListPrice(product) {
  if (!product) return null;

  let listPrice = null;
  let salePrice = null;
  const parsed = parsePriceProcessed(product);

  if (parsed) {
    listPrice = isNumber(parsed.listPrice) ? parsed.listPrice : null;
    salePrice = isNumber(parsed.salePrice) ? parsed.salePrice : null;
  }

  if (listPrice != null && salePrice != null && listPrice > salePrice) {
    return listPrice;
  }

  const rrp = listPrice ?? salePrice ?? resolveScapiBookPrice(product) ?? null;
  const effective = getSimplePdpCoverFlyoutEffectivePrice(product);

  if (rrp != null && effective != null && rrp > effective) {
    return rrp;
  }

  return null;
}

/** True when `c_allVariantsData` entry has no sale or list book price. */
export function isCoverVariantBookPriceMissing(variant) {
  if (!variant) return true;

  return variant.salePrice == null && variant.price == null;
}

/** Whether selections include at least one configured option slot (excludes cover). */
export function hasCustomizeOptionSelections(selections) {
  if (!selections || typeof selections !== 'object') return false;

  return Object.entries(selections).some(
    ([key, val]) => key !== 'cover' &&
      key !== 'optionalAddOns' &&
      Array.isArray(val) &&
      val.some((slot) => slot?.productId)
  );
}

/**
 * Merge SCAPI-fetched cover pricing into the draft cover selection.
 *
 * @param {Object|null|undefined} cover
 * @param {Object|null|undefined} fetchedProduct
 * @param {{promoOnly?: boolean}} [options] - Configurable Master: only patch promo when it
 *   beats the sale-book price (mirrors CustomizeJourneyContext). Freeform: patch full effective.
 */
export function enrichSimplePdpCoverFlyoutSelection(cover, fetchedProduct, {promoOnly = false} = {}) {
  if (!cover?.productId || !fetchedProduct) return cover;

  const patch = {};
  const promoPrice = getPromotionalPrice(fetchedProduct);
  const currentSale = cover.salePrice ?? cover.price ?? null;

  if (promoOnly) {
    if (currentSale == null) {
      const effectivePrice = getSimplePdpCoverFlyoutEffectivePrice(fetchedProduct);
      const listPrice = getSimplePdpCoverFlyoutListPrice(fetchedProduct);

      if (effectivePrice != null) {
        patch.salePrice = effectivePrice;
      }

      if (listPrice != null && cover.price == null) {
        patch.price = listPrice;
      }
    } else {
      if (promoPrice != null && promoPrice < currentSale) {
        patch.salePrice = promoPrice;
      }

      if (cover.price == null) {
        const listPrice = getSimplePdpCoverFlyoutListPrice(fetchedProduct);

        if (listPrice != null) {
          patch.price = listPrice;
        }
      }
    }
  } else {
    const listPrice = getSimplePdpCoverFlyoutListPrice(fetchedProduct);
    const effectivePrice = getSimplePdpCoverFlyoutEffectivePrice(fetchedProduct);

    if (listPrice != null && (cover.price == null || listPrice > (cover.price ?? 0))) {
      patch.price = listPrice;
    }

    if (effectivePrice != null && (currentSale == null || effectivePrice < currentSale)) {
      patch.salePrice = effectivePrice;
    }
  }

  if (Object.keys(patch).length === 0) return cover;

  return {...cover, ...patch};
}

/**
 * Resolve sale/list from a catalog variant (`c_allVariantsData` entry) before SCAPI fetch completes.
 *
 * @param {Object|null|undefined} variant
 * @returns {{salePrice: number, price: number|null}}
 */
export function resolveCoverCatalogVariantPricing(variant) {
  if (!variant) {
    return {salePrice: 0, price: null};
  }

  const effectivePrice = getSimplePdpCoverFlyoutEffectivePrice(variant);
  const listPrice = getSimplePdpCoverFlyoutListPrice(variant);
  const salePrice = effectivePrice ?? variant.salePrice ?? variant.price ?? 0;
  const price =
    listPrice != null && salePrice != null && listPrice > salePrice ?
      listPrice :
      variant.price ?? listPrice ?? null;

  return {
    salePrice: salePrice ?? 0,
    price,
  };
}
